import { defineComponent as _defineComponent } from 'vue';
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeClass as _normalizeClass, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = { class: "max-w-7xl mx-auto px-6 lg:px-8" };
const _hoisted_2 = { class: "flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10" };
const _hoisted_3 = { class: "space-y-3" };
const _hoisted_4 = {
    key: 0,
    class: "text-3xl lg:text-4xl font-bold text-gray-900"
};
const _hoisted_5 = {
    key: 1,
    class: "text-base lg:text-lg text-gray-600 max-w-2xl"
};
const _hoisted_6 = { class: "relative" };
const _hoisted_7 = {
    key: 0,
    class: "bg-white/95 border border-white/60 rounded-3xl p-8 text-center shadow-xl"
};
const _hoisted_8 = { class: "text-red-600 font-medium mb-4" };
const _hoisted_9 = {
    key: 1,
    class: "relative"
};
const _hoisted_10 = { class: "relative" };
const _hoisted_11 = ["onClick"];
const _hoisted_12 = ["src", "alt", "onError"];
const _hoisted_13 = { key: 0 };
const _hoisted_14 = { key: 1 };
const _hoisted_15 = ["onClick"];
const _hoisted_16 = ["aria-label", "disabled"];
const _hoisted_17 = ["aria-label", "disabled"];
const _hoisted_18 = {
    key: 0,
    class: "mt-6 flex justify-center gap-2"
};
const _hoisted_19 = ["aria-label", "onClick"];
const _hoisted_20 = {
    key: 1,
    class: "bg-white/95 border border-white/60 rounded-3xl p-8 text-center shadow-xl"
};
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useProductCardStyles } from '../composables/useProductCardStyles';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProductSlider',
    props: {
        title: { default: 'Unsere Highlights' },
        subtitle: {},
        type: { default: 'default' },
        limit: { default: 8 },
        categoryId: {},
        categorySlug: {},
        tag: {},
        includeUnavailable: { type: Boolean, default: false },
        lazy: { type: Boolean, default: true },
        showControls: { type: Boolean, default: true },
        variant: { default: 'default' },
        exclude: {},
        popularDays: {},
        styleKey: {},
        cardStyleOverrides: {},
        buttonLabel: { default: 'Details' }
    },
    emits: ["productSelected"],
    setup(__props, { emit: __emit }) {
        const currencyFormatter = new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        });
        const props = __props;
        const emit = __emit;
        const apiClient = inject('apiClient');
        if (!apiClient) {
            throw new Error('ProductSlider requires RestaurantApiClient via provide/inject');
        }
        const rootEl = ref(null);
        const sliderTrack = ref(null);
        const observer = ref(null);
        const autoRotateTimer = ref(null);
        const items = ref([]);
        const meta = ref(null);
        const cacheInfo = ref();
        const isLoading = ref(false);
        const error = ref(null);
        const hasIntersected = ref(false);
        const brokenImages = ref({});
        const currentSlide = ref(0);
        const itemsPerSlide = ref(1);
        const hasItems = computed(() => items.value.length > 0);
        const subtitleToShow = computed(() => props.subtitle || meta.value?.category?.name || '');
        const titleText = computed(() => props.title || 'Produkt-Slider');
        const previousLabel = computed(() => `Zurück (${titleText.value})`);
        const nextLabel = computed(() => `Weiter (${titleText.value})`);
        const slides = computed(() => {
            const perSlide = Math.max(itemsPerSlide.value, 1);
            if (!items.value.length) {
                return [];
            }
            const grouped = [];
            for (let index = 0; index < items.value.length; index += perSlide) {
                grouped.push(items.value.slice(index, index + perSlide));
            }
            return grouped;
        });
        const totalSlides = computed(() => slides.value.length);
        const trackStyle = computed(() => ({
            transform: `translateX(-calc(${currentSlide.value * 100}% - 1rem))`
        }));
        const canGoPrev = computed(() => currentSlide.value > 0);
        const canGoNext = computed(() => currentSlide.value < totalSlides.value - 1);
        const skeletonSlides = computed(() => {
            const perSlide = Math.max(itemsPerSlide.value, 1);
            const total = Math.max(props.limit, perSlide);
            const slideCount = Math.ceil(total / perSlide);
            return Array.from({ length: slideCount }, (_, index) => index);
        });
        const skeletonCards = computed(() => Array.from({ length: Math.max(itemsPerSlide.value, 1) }, (_, index) => index));
        const { cardStyles } = useProductCardStyles({
            styleKey: computed(() => props.styleKey),
            overrides: computed(() => props.cardStyleOverrides)
        });
        const resolvedButtonLabel = computed(() => props.buttonLabel || 'Details');
        const sectionClasses = computed(() => {
            switch (props.variant) {
                case 'highlight':
                    return 'py-16 bg-gradient-to-b from-brand-pink-50 via-white to-white';
                case 'minimal':
                    return 'py-10';
                default:
                    return 'py-14 bg-neutral-50';
            }
        });
        const buildRequest = () => ({
            type: props.type,
            limit: props.limit,
            categoryId: props.categoryId,
            categorySlug: props.categorySlug,
            tag: props.tag,
            includeUnavailable: props.includeUnavailable,
            popularDays: props.popularDays
        });
        const productKey = (item) => String(item._id || item.id);
        const hasBrokenImage = (item) => Boolean(brokenImages.value[productKey(item)]);
        const markImageAsBroken = (item) => {
            const key = productKey(item);
            brokenImages.value = { ...brokenImages.value, [key]: true };
        };
        const getPriceLabel = (item) => {
            if (item.priceRange && typeof item.priceRange.min === 'number') {
                return `ab ${currencyFormatter.format(item.priceRange.min)}`;
            }
            if (typeof item.priceCent === 'number') {
                return currencyFormatter.format(item.priceCent / 100);
            }
            if (typeof item.price === 'number') {
                return currencyFormatter.format(item.price);
            }
            return 'Preis auf Anfrage';
        };
        const getOriginalPriceLabel = (item) => {
            if (typeof item.originalPriceCent === 'number') {
                return currencyFormatter.format(item.originalPriceCent / 100);
            }
            if (typeof item.originalPrice === 'number') {
                return currencyFormatter.format(item.originalPrice);
            }
            return '';
        };
        const handleSelect = (item) => {
            emit('productSelected', item);
        };
        const startAutoRotate = () => {
            stopAutoRotate();
            if (totalSlides.value > 1) {
                autoRotateTimer.value = setInterval(() => {
                    if (canGoNext.value) {
                        currentSlide.value += 1;
                    }
                    else {
                        currentSlide.value = 0;
                    }
                }, 5000); // Rotate every 5 seconds
            }
        };
        const stopAutoRotate = () => {
            if (autoRotateTimer.value) {
                clearInterval(autoRotateTimer.value);
                autoRotateTimer.value = null;
            }
        };
        const resetAutoRotate = () => {
            startAutoRotate();
        };
        const nextSlide = () => {
            if (canGoNext.value) {
                currentSlide.value += 1;
                resetAutoRotate();
            }
        };
        const prevSlide = () => {
            if (canGoPrev.value) {
                currentSlide.value -= 1;
                resetAutoRotate();
            }
        };
        const goToSlide = (index) => {
            if (index >= 0 && index < totalSlides.value) {
                currentSlide.value = index;
                resetAutoRotate();
            }
        };
        const updateItemsPerSlide = () => {
            if (typeof window === 'undefined') {
                itemsPerSlide.value = 1;
                return;
            }
            const width = window.innerWidth;
            if (width >= 1280) {
                itemsPerSlide.value = 3;
                return;
            }
            if (width >= 1024) {
                itemsPerSlide.value = 3;
                return;
            }
            if (width >= 768) {
                itemsPerSlide.value = 2;
                return;
            }
            itemsPerSlide.value = 1;
        };
        const handleResize = () => {
            updateItemsPerSlide();
        };
        let fetchSequence = 0;
        const fetchProducts = async (force = false) => {
            if (props.lazy && !force && !hasIntersected.value) {
                return;
            }
            const payload = buildRequest();
            const sequenceId = ++fetchSequence;
            isLoading.value = true;
            error.value = null;
            try {
                const response = await apiClient.getProductSlider(payload);
                if (fetchSequence !== sequenceId) {
                    return;
                }
                if (response.status === 'success' && response.data) {
                    const data = response.data;
                    let fetchedItems = data.items || [];
                    // Filter out excluded products
                    if (props.exclude && props.exclude.length > 0) {
                        fetchedItems = fetchedItems.filter(item => !props.exclude.includes(item._id || item.id));
                    }
                    items.value = fetchedItems;
                    meta.value = data.meta || null;
                    cacheInfo.value = data.cache;
                    brokenImages.value = {};
                    currentSlide.value = 0;
                    await nextTick();
                    // Start auto-rotation after products are loaded
                    startAutoRotate();
                }
                else {
                    error.value = response.message || 'Produkte konnten nicht geladen werden.';
                }
            }
            catch (err) {
                if (fetchSequence === sequenceId) {
                    error.value = err?.message || 'Unbekannter Fehler beim Laden der Produkte.';
                }
            }
            finally {
                if (fetchSequence === sequenceId) {
                    isLoading.value = false;
                }
            }
        };
        const setupObserver = () => {
            if (!props.lazy) {
                fetchProducts(true);
                return;
            }
            if (typeof window === 'undefined' || !rootEl.value) {
                fetchProducts(true);
                return;
            }
            observer.value = new IntersectionObserver((entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting) {
                    hasIntersected.value = true;
                    observer.value?.disconnect();
                    fetchProducts(true);
                }
            }, { threshold: 0.2 });
            observer.value.observe(rootEl.value);
        };
        watch(() => totalSlides.value, () => {
            if (totalSlides.value === 0) {
                currentSlide.value = 0;
                return;
            }
            if (currentSlide.value > totalSlides.value - 1) {
                currentSlide.value = totalSlides.value - 1;
            }
        });
        watch(() => items.value.length, () => {
            currentSlide.value = 0;
        });
        watch(() => [
            props.type,
            props.limit,
            props.categoryId,
            props.categorySlug,
            props.tag,
            props.includeUnavailable,
            props.popularDays
        ], () => {
            if (props.lazy && !hasIntersected.value) {
                return;
            }
            fetchProducts(true);
        });
        onMounted(() => {
            setupObserver();
            updateItemsPerSlide();
            if (typeof window !== 'undefined') {
                window.addEventListener('resize', handleResize);
            }
        });
        onBeforeUnmount(() => {
            stopAutoRotate();
            observer.value?.disconnect();
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleResize);
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("section", {
                ref_key: "rootEl",
                ref: rootEl,
                class: _normalizeClass(sectionClasses.value)
            }, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _renderSlot(_ctx.$slots, "heading", {
                                meta: meta.value,
                                isLoading: isLoading.value,
                                items: items.value
                            }, () => [
                                (_ctx.title)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_ctx.title), 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true),
                                (subtitleToShow.value)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(subtitleToShow.value), 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true)
                            ])
                        ])
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                        (error.value && !isLoading.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _renderSlot(_ctx.$slots, "error", { error: error.value }, () => [
                                    _createElementVNode("p", _hoisted_8, _toDisplayString(error.value), 1 /* TEXT */),
                                    _createElementVNode("button", {
                                        type: "button",
                                        class: "inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-pink-500 to-brand-violet-500 px-5 py-2 text-white font-semibold shadow-lg",
                                        onClick: _cache[0] || (_cache[0] = ($event) => (fetchProducts(true)))
                                    }, " Erneut versuchen ")
                                ])
                            ]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                _createElementVNode("div", _hoisted_10, [
                                    _createElementVNode("div", {
                                        class: "overflow-hidden py-4 px-2",
                                        onMouseenter: stopAutoRotate,
                                        onMouseleave: startAutoRotate
                                    }, [
                                        _createElementVNode("div", {
                                            ref_key: "sliderTrack",
                                            ref: sliderTrack,
                                            class: "flex transition-transform duration-500 ease-out gap-1",
                                            style: _normalizeStyle(trackStyle.value)
                                        }, [
                                            (isLoading.value)
                                                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(skeletonSlides.value, (slideIndex) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                        key: `placeholder-slide-${slideIndex}`,
                                                        class: "flex min-w-full gap-6 justify-center md:justify-start"
                                                    }, [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(skeletonCards.value, (placeholderIndex) => {
                                                            return (_openBlock(), _createElementBlock("div", {
                                                                key: `placeholder-${slideIndex}-${placeholderIndex}`,
                                                                class: "flex-1 min-w-0 max-w-xs basis-72 sm:basis-80 flex-shrink-0"
                                                            }, [...(_cache[1] || (_cache[1] = [
                                                                    _createStaticVNode("<div class=\"h-full animate-pulse bg-white/70 backdrop-blur-sm border border-white/60 rounded-3xl overflow-hidden\"><div class=\"aspect-square bg-gradient-to-br from-brand-pink-500/30 to-brand-violet-500/30\"></div><div class=\"p-5 space-y-3\"><div class=\"h-4 bg-gray-200 rounded-full w-3/4\"></div><div class=\"h-4 bg-gray-100 rounded-full w-1/2\"></div><div class=\"h-10 bg-gray-100 rounded-xl w-full\"></div></div></div>", 1)
                                                                ]))]));
                                                        }), 128 /* KEYED_FRAGMENT */))
                                                    ]));
                                                }), 128 /* KEYED_FRAGMENT */))
                                                : (hasItems.value)
                                                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(slides.value, (slideItems, slideIndex) => {
                                                        return (_openBlock(), _createElementBlock("div", {
                                                            key: `slide-${slideIndex}`,
                                                            class: "flex min-w-full gap-6 justify-center"
                                                        }, [
                                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slideItems, (item) => {
                                                                return (_openBlock(), _createElementBlock("div", {
                                                                    key: productKey(item),
                                                                    class: _normalizeClass(_unref(cardStyles).wrapper)
                                                                }, [
                                                                    _renderSlot(_ctx.$slots, "card", {
                                                                        item: item,
                                                                        meta: meta.value,
                                                                        select: () => handleSelect(item)
                                                                    }, () => [
                                                                        _createElementVNode("div", {
                                                                            class: _normalizeClass(_unref(cardStyles).card),
                                                                            onClick: ($event) => (handleSelect(item))
                                                                        }, [
                                                                            _createElementVNode("div", {
                                                                                class: _normalizeClass(_unref(cardStyles).imageWrapper)
                                                                            }, [
                                                                                (item.image && !hasBrokenImage(item))
                                                                                    ? (_openBlock(), _createElementBlock("img", {
                                                                                        key: 0,
                                                                                        src: item.image,
                                                                                        alt: item.name,
                                                                                        loading: "lazy",
                                                                                        class: _normalizeClass(_unref(cardStyles).image),
                                                                                        onError: () => markImageAsBroken(item)
                                                                                    }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_12))
                                                                                    : (_openBlock(), _createElementBlock("div", {
                                                                                        key: 1,
                                                                                        class: _normalizeClass(_unref(cardStyles).placeholder)
                                                                                    }, _toDisplayString(item.categoryName || meta.value?.category?.name || 'Produkt'), 3 /* TEXT, CLASS */)),
                                                                                (item.discountPercentage)
                                                                                    ? (_openBlock(), _createElementBlock("div", {
                                                                                        key: 2,
                                                                                        class: _normalizeClass([_unref(cardStyles).badges.leftBase, _unref(cardStyles).badges.discount])
                                                                                    }, " -" + _toDisplayString(item.discountPercentage) + "% ", 3 /* TEXT, CLASS */))
                                                                                    : (item.popular)
                                                                                        ? (_openBlock(), _createElementBlock("div", {
                                                                                            key: 3,
                                                                                            class: _normalizeClass([_unref(cardStyles).badges.leftBase, _unref(cardStyles).badges.popular])
                                                                                        }, " Beliebt ", 2 /* CLASS */))
                                                                                        : _createCommentVNode("v-if", true),
                                                                                (item.isOnSale)
                                                                                    ? (_openBlock(), _createElementBlock("div", {
                                                                                        key: 4,
                                                                                        class: _normalizeClass([_unref(cardStyles).badges.rightBase, _unref(cardStyles).badges.sale])
                                                                                    }, " Deal ", 2 /* CLASS */))
                                                                                    : _createCommentVNode("v-if", true)
                                                                            ], 2 /* CLASS */),
                                                                            _createElementVNode("div", {
                                                                                class: _normalizeClass(_unref(cardStyles).content)
                                                                            }, [
                                                                                _createElementVNode("div", {
                                                                                    class: _normalizeClass(_unref(cardStyles).infoStack)
                                                                                }, [
                                                                                    _createElementVNode("div", {
                                                                                        class: _normalizeClass(_unref(cardStyles).titleRow)
                                                                                    }, [
                                                                                        _createElementVNode("p", {
                                                                                            class: _normalizeClass(_unref(cardStyles).title)
                                                                                        }, _toDisplayString(item.name), 3 /* TEXT, CLASS */),
                                                                                        _createElementVNode("span", {
                                                                                            class: _normalizeClass(_unref(cardStyles).price)
                                                                                        }, _toDisplayString(getPriceLabel(item)), 3 /* TEXT, CLASS */)
                                                                                    ], 2 /* CLASS */),
                                                                                    (item.originalPriceCent || item.originalPrice)
                                                                                        ? (_openBlock(), _createElementBlock("p", {
                                                                                            key: 0,
                                                                                            class: _normalizeClass(_unref(cardStyles).originalPrice)
                                                                                        }, _toDisplayString(getOriginalPriceLabel(item)), 3 /* TEXT, CLASS */))
                                                                                        : _createCommentVNode("v-if", true),
                                                                                    (item.description)
                                                                                        ? (_openBlock(), _createElementBlock("p", {
                                                                                            key: 1,
                                                                                            class: _normalizeClass(_unref(cardStyles).description)
                                                                                        }, _toDisplayString(item.description), 3 /* TEXT, CLASS */))
                                                                                        : _createCommentVNode("v-if", true)
                                                                                ], 2 /* CLASS */),
                                                                                _createElementVNode("div", {
                                                                                    class: _normalizeClass(_unref(cardStyles).footer)
                                                                                }, [
                                                                                    _createElementVNode("div", {
                                                                                        class: _normalizeClass(_unref(cardStyles).category)
                                                                                    }, [
                                                                                        (item.categoryName)
                                                                                            ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(item.categoryName), 1 /* TEXT */))
                                                                                            : (meta.value?.category?.name)
                                                                                                ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(meta.value?.category?.name), 1 /* TEXT */))
                                                                                                : _createCommentVNode("v-if", true)
                                                                                    ], 2 /* CLASS */),
                                                                                    _createElementVNode("button", {
                                                                                        type: "button",
                                                                                        class: _normalizeClass(_unref(cardStyles).button),
                                                                                        onClick: _withModifiers(($event) => (handleSelect(item)), ["stop"])
                                                                                    }, _toDisplayString(resolvedButtonLabel.value), 11 /* TEXT, CLASS, PROPS */, _hoisted_15)
                                                                                ], 2 /* CLASS */)
                                                                            ], 2 /* CLASS */)
                                                                        ], 10 /* CLASS, PROPS */, _hoisted_11)
                                                                    ])
                                                                ], 2 /* CLASS */));
                                                            }), 128 /* KEYED_FRAGMENT */))
                                                        ]));
                                                    }), 128 /* KEYED_FRAGMENT */))
                                                    : _createCommentVNode("v-if", true)
                                        ], 4 /* STYLE */)
                                    ], 32 /* NEED_HYDRATION */),
                                    (_ctx.showControls && !isLoading.value && totalSlides.value > 1)
                                        ? (_openBlock(), _createElementBlock("button", {
                                            key: 0,
                                            type: "button",
                                            class: _normalizeClass(["absolute left-0 top-1/2 -translate-y-1/2 rounded-full border border-neutral-200 bg-white p-2 transition-colors dark:border-neutral-800 dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800", { 'opacity-40 cursor-not-allowed': !canGoPrev.value }]),
                                            "aria-label": previousLabel.value,
                                            disabled: !canGoPrev.value,
                                            onClick: prevSlide
                                        }, [...(_cache[2] || (_cache[2] = [
                                                _createElementVNode("svg", {
                                                    class: "h-6 w-6 text-black dark:text-white",
                                                    fill: "none",
                                                    stroke: "currentColor",
                                                    viewBox: "0 0 24 24"
                                                }, [
                                                    _createElementVNode("path", {
                                                        "stroke-linecap": "round",
                                                        "stroke-linejoin": "round",
                                                        "stroke-width": "2",
                                                        d: "M15 19l-7-7 7-7"
                                                    })
                                                ], -1 /* CACHED */)
                                            ]))], 10 /* CLASS, PROPS */, _hoisted_16))
                                        : _createCommentVNode("v-if", true),
                                    (_ctx.showControls && !isLoading.value && totalSlides.value > 1)
                                        ? (_openBlock(), _createElementBlock("button", {
                                            key: 1,
                                            type: "button",
                                            class: _normalizeClass(["absolute right-0 top-1/2 -translate-y-1/2 rounded-full border border-neutral-200 bg-white p-2 transition-colors dark:border-neutral-800 dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800", { 'opacity-40 cursor-not-allowed': !canGoNext.value }]),
                                            "aria-label": nextLabel.value,
                                            disabled: !canGoNext.value,
                                            onClick: nextSlide
                                        }, [...(_cache[3] || (_cache[3] = [
                                                _createElementVNode("svg", {
                                                    class: "h-6 w-6 text-black dark:text-white",
                                                    fill: "none",
                                                    stroke: "currentColor",
                                                    viewBox: "0 0 24 24"
                                                }, [
                                                    _createElementVNode("path", {
                                                        "stroke-linecap": "round",
                                                        "stroke-linejoin": "round",
                                                        "stroke-width": "2",
                                                        d: "M9 5l7 7-7 7"
                                                    })
                                                ], -1 /* CACHED */)
                                            ]))], 10 /* CLASS, PROPS */, _hoisted_17))
                                        : _createCommentVNode("v-if", true)
                                ]),
                                (_ctx.showControls && !isLoading.value && totalSlides.value > 1)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, (_, index) => {
                                            return (_openBlock(), _createElementBlock("button", {
                                                key: `indicator-${index}`,
                                                type: "button",
                                                class: _normalizeClass(["h-2 rounded-full transition-all duration-300", currentSlide.value === index ? 'bg-black dark:bg-white w-8' : 'bg-neutral-300 dark:bg-neutral-700 w-2']),
                                                "aria-label": `Gehe zu Slide ${index + 1}`,
                                                onClick: ($event) => (goToSlide(index))
                                            }, null, 10 /* CLASS, PROPS */, _hoisted_19));
                                        }), 128 /* KEYED_FRAGMENT */))
                                    ]))
                                    : _createCommentVNode("v-if", true),
                                (!isLoading.value && !hasItems.value && !error.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                        _renderSlot(_ctx.$slots, "empty", { meta: meta.value }, () => [
                                            _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-gray-600" }, "Keine Produkte gefunden.", -1 /* CACHED */))
                                        ])
                                    ]))
                                    : _createCommentVNode("v-if", true)
                            ]))
                    ])
                ])
            ], 2 /* CLASS */));
        };
    }
});
